import React from 'react';
import PrimaryButton from '../shared/button/PrimaryButton';
import { ReactComponent as UploadIcon } from '../../assets/icons/Upload.svg';
import axios, { AxiosError } from 'axios';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { BaseImportMetadata, User } from '@esg/esg-global-types';
import { ImportType } from './ImportTypeSelect';
import ConfirmationModal from '../shared/modal/ConfirmationModal';
import { generateFileUploadRequest } from '../../lib/file_upload/uploadRequestPrep';
import { API_ENDPOINT, GCP_FILE_UPLOAD_BUCKET } from '../../env';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import {
  DATA_JOB_FAILURE,
  FEEDBACK_ACTION_FAILURE,
  FUNCTION_UNKNOWN_FAILURE
} from '../../util/global_variables';
import { nowISOString } from '../../util/date';
import { getAuthHeaderForUser, handleAuthError } from '../../lib/auth/auth.util';

export type ImportContext = Record<string, unknown>;

export interface ImportContextProps {
  handleContextChange: (context: ImportContext) => void;
  handleContextValid: (valid: boolean) => void;
}

interface ImportRequestData {
  metadata: BaseImportMetadata;
  data: {
    params: ImportContext;
  };
}

interface ImportDataButtonProps {
  user: User;
  import_type: ImportType;
  context: ImportContext;
  label?: string;
  disabled?: boolean;
}

const SOURCE = 'ImportDataButton.tsx';

/**
 * Reusable Button to import various kinds of application data via a micro-service cloud function.
 * @param {User} user The user making the import request for metadata.
 * @param {ImportType} import_type Selected import type containing a microservice endpoint and other relevant data.
 * @param {ImportContext} context Dynamic data passed to cloud function endpoint to be used for importing logic.
 * @param {ImportErrors} errors Error map from parent used to enable or disable button.
 * @param {string | undefined} label Optional custom label to use for Button, defaults to Import Data.
 * @param {boolean | undefined} disabled Optionally disable import button based on validation from parent.
 * @returns {JSX.Element}
 */
const ImportDataButton = ({
  user,
  import_type,
  context,
  disabled,
  label
}: ImportDataButtonProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [displayConfirmModal, setDisplayConfirmModal] = React.useState<boolean>(false);
  const [importLoading, setImportLoading] = React.useState<boolean>(false);

  const handleImportClick = (): void => {
    setDisplayConfirmModal(true);
  };

  /**
   * Concurrently upload all files required by the given import type, fetched from the given corresponding upload context.
   * @param {Array<string>} required_file_keys Field keys to get file data from in the import context, throw error if data not found under key.
   * @param {string} tracking_id Tracking ID of import to use for GCP upload path of each file.
   * @returns {Promise<void>}
   */
  const upload_required_files = async (
    required_file_keys: Array<string>,
    tracking_id: string
  ): Promise<void> => {
    if (group && company) {
      const file_upload_promises: Array<Promise<void>> = required_file_keys.map(
        async (file_key: string) => {
          let file: File;
          const file_value: unknown = context[file_key];
          // Need to get valid file from context value or generator function.
          if (!file_value) {
            throw new Error(`Import Context missing file dependency: ${file_key}`);
          }
          if (file_value instanceof File) {
            file = file_value;
          } else if (typeof file_value === 'function') {
            const result: File = await (file_value as () => Promise<File>)();
            if (result instanceof File) {
              file = result;
            } else {
              throw new Error(`Function provided for ${file_key} did not return a File.`);
            }
          } else {
            throw new Error(
              `Data supplied to ${file_key} was not of required type: File or File Function`
            );
          }
          const file_upload_data: FormData = generateFileUploadRequest(
            file,
            file_key,
            atob(GCP_FILE_UPLOAD_BUCKET),
            `${group.id}/${company.id}/imports/${import_type.id}/${tracking_id}`,
            'false',
            'false'
          );
          // Send file to backend via HTTP request for further processing.
          await axios({
            method: 'post',
            url: `${API_ENDPOINT}/upload_file`,
            data: file_upload_data,
            headers: {
              ...(await getAuthHeaderForUser())
            }
          });
        }
      );
      await Promise.all(file_upload_promises);
    }
  };

  // Send the import context data to the endpoint and handle response.
  const handleImport = async (): Promise<void> => {
    const tracking_id: string = generateUuidV4();
    setImportLoading(true);
    try {
      // Check given source identifier from relative context.
      if (context.source !== undefined && typeof context.source !== 'string') {
        throw new Error('Invalid source field data given for import context.');
      }

      // Import Cloud Function might require files to be uploaded to GCS.
      const required_file_keys: Array<string> | undefined = import_type.file_upload_dependancies;
      if (required_file_keys && required_file_keys.length > 0) {
        await upload_required_files(required_file_keys, tracking_id);
      }

      // Call importing Cloud Function directly with relevant context and metadata.
      const import_request_data: ImportRequestData = {
        metadata: {
          tracking_id: tracking_id,
          ...(context.source !== undefined && { source: context.source }),
          import_type: import_type.id,
          started: nowISOString(),
          requested_by: {
            id: user.id,
            name: `${user.first_name} ${user.last_name}`
          }
        },
        data: {
          params: context
        }
      };

      axios({
        method: 'post',
        url: import_type.endpoint,
        data: import_request_data,
        headers: {
          ...(await getAuthHeaderForUser())
        }
      }).catch((error: AxiosError) => {
        log(
          'error',
          new MetadataError(
            error instanceof Error
              ? error.message
              : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleImport'),
            {
              user: user,
              import_type: import_type,
              context: context
            },
            tracking_id
          )
        );
        setFeedbackData({
          message: FEEDBACK_ACTION_FAILURE(DATA_JOB_FAILURE('Import'), tracking_id),
          state: true,
          type: 'error'
        });
        handleAuthError(error);
      });
    } catch (err: unknown) {
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleImport'),
          {
            user: user,
            import_type: import_type,
            context: context
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE(DATA_JOB_FAILURE('Import'), tracking_id),
        state: true,
        type: 'error'
      });
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        open={displayConfirmModal}
        title={`Are you sure you want to perform this ${import_type.name} Import?`}
        description={import_type.description}
        confirm_button_text="Import Data"
        handleConfirm={async () => await handleImport()}
        confirm_loading={importLoading}
        handleCloseModal={() => setDisplayConfirmModal(false)}
      />
      <PrimaryButton
        startIcon={<UploadIcon height={25} fill="none" stroke="currentColor" strokeWidth={4} />}
        sx={{ minWidth: 150, my: 2 }}
        onClick={handleImportClick}
        loading={importLoading}
        disabled={disabled}
      >
        {label ?? 'Import Data'}
      </PrimaryButton>
    </>
  );
};

export default ImportDataButton;
